import { useState } from 'react';
import cn from 'classnames';
import { Banknote, ChevronDown, Truck, X, Zap } from 'lucide-react';
import i18n from '@/settings/i18n';
import { FAQS } from './Faqs';

const t = i18n.init('buyback');

const STEPS = [
  {
    title: 'Get a quote',
    description:
      'Answer a few quick questions and get a guaranteed quote in under a minute.',
    Icon: Zap,
  },
  {
    title: 'Ship for free',
    description:
      'Pack your device and send it with the prepaid label sent to your email.',
    Icon: Truck,
  },
  {
    title: 'Get paid',
    description:
      'Once your device is inspected and the offer is confirmed, get paid via bank transfer or PayPal.',
    Icon: Banknote,
  },
] as const;

// The questions the purchase flow answers, out of the buyback page's.
const QUESTIONS = [
  'What devices can I sell on Reebelo?',
  'How is my quote calculated?',
  'How do I ship my device?',
  'When and how do I get paid?',
  'What happens to the data on my device?',
];
const FAQ_ITEMS = FAQS.filter((faq) => QUESTIONS.includes(faq.question));

/**
 * "How Trade-in works", opened from the info button by the PDP's trade-in:
 * the three steps, the common questions, and the way on to the widget.
 */
export const TradeInInfoContent = ({
  onClose,
  onAdd,
}: {
  onClose: () => void;
  onAdd: () => void;
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <>
      <div className="flex h-full flex-col text-sm text-gray-700">
        <div className="flex items-center justify-between gap-4 px-6 py-5">
          <h2 className="text-lg font-medium">{t`How Trade-in works`}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t`Close`}
            className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-700 text-white hover:bg-gray-700/90"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <ul className="flex flex-col gap-7 bg-[linear-gradient(90deg,_#ECFAF4_0%,_#F7FDFA_70%,_#FFF_100%)] px-6 py-6">
          {STEPS.map(({ title, description, Icon }) => (
            <li key={title} className="flex items-start gap-4">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-700 text-white">
                <Icon className="h-4 w-4" />
              </span>
              <span>
                <span className="block font-bold">{t(title)}</span>
                <span className="block text-gray-700/60">{t(description)}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="px-6 pt-8">
          <h3 className="text-lg font-medium">{t`Frequently Asked Questions`}</h3>
          <div className="mt-2">
            {FAQ_ITEMS.map((faq, index) => (
              <div
                key={faq.question}
                className="border-b border-[#E6E7EB] last:border-b-0"
              >
                <button
                  type="button"
                  aria-expanded={openIndex === index}
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                  className="flex w-full items-center justify-between gap-3 py-4 text-left text-sm"
                >
                  <span className={cn({ 'font-medium': openIndex === index })}>
                    {t(faq.question)}
                  </span>
                  <ChevronDown
                    className={cn('h-4 w-4 shrink-0 transition-transform', {
                      'rotate-180': openIndex === index,
                    })}
                    strokeWidth={1.5}
                  />
                </button>
                <div
                  className={cn(
                    'grid transition-[grid-template-rows] duration-300 ease-in-out',
                    openIndex === index ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                  )}
                >
                  <div className="overflow-hidden">
                    <div
                      className="pb-4 text-sm text-gray-700/70 [&_li]:ml-5 [&_ul]:list-disc"
                      // Our own copy: the buyback page's answers.
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: t(faq.answer) }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 mt-auto border-t border-[#E6E7EB] bg-white px-6 py-4">
          <button
            type="button"
            onClick={onAdd}
            className="h-14 w-full rounded-lg bg-gray-700 text-base font-medium text-white transition duration-150 hover:bg-gray-700/90"
          >
            {t`Add a trade-in`}
          </button>
        </div>
      </div>
    </>
  );
};
